import { createContext, useContext, useState } from "react";
import * as React from "react";
import {
  SMResult,
  StackMapResponse,
  StackMapShadowData,
} from "@tlc/ls2pac/app/utils/StackMapTypes";
import { LS2PAC_CONSTANTS } from "../../main/constants";
import { useLocationConfiguration } from "./LocationConfiguration";
import { TLCEventManager } from "../events/EventManager";
import { ResourceDWR } from "../dto/ResourceDWR";
import { HoldingsInformation } from "../dto/HoldingsInformation";
import { ItemAvailability } from "./pages/resource/ItemAvailability";

export interface Stackmap {
  init: (shadowRoot: ShadowRoot | HTMLElement) => Promise<any>;
  getMapItData: (
    requestData: StackMapShadowData[]
  ) => Promise<StackMapResponse>;
  /**
   *
   * @param result should only be passing a single SMResult - temporarily need to wrap in array structure
   * @param container
   * @returns
   */
  render: (
    result: { results: SMResult[] },
    container: HTMLElement
  ) => SMResult["uuid"];
  destroy: (uuid: StackMapShadowData["uuid"]) => void;
}

type GetDisplayMapItButtonFunction = (
  request: StackMapShadowData[]
) => Promise<Map<SMResult["uuid"], boolean>>;

type OpenMapItDialogFunction = (uuid: SMResult["uuid"]) => void;
interface StackMapContextType {
  StackMap: Stackmap | null;
  getDisplayMapItButtons: GetDisplayMapItButtonFunction;
  openMapItDialog: OpenMapItDialogFunction;
  loading: boolean;
  error: Error | null;
  stackMapEnabled: boolean;
}

const StackMapContext = createContext<StackMapContextType>(null);

interface StackMapScript {
  StackMap: Stackmap;
  shadowRoot: ShadowRoot;
  dialogContainer: HTMLElement;
}

export const StackMapScriptHolder = (() => {
  let StackMap: Stackmap;
  let pendingScriptRequest: Promise<StackMapScript>;
  let openId: SMResult["uuid"] | null = null;
  let stackMapDialogContainer: HTMLElement | null = null;
  const SMResultMap = new Map<SMResult["uuid"], SMResult>();
  // const { globalSettings } = useGlobalConfig();

  // TODO this is a temporary workaround until Resource pages can be integrated with React Router
  const timeoutListenerId = TLCEventManager.addListener(
    LS2PAC_CONSTANTS.Events.onTimeout,
    () => {
      console.log("Page timeout event occurred (StackMap), uuid:", openId);
      if (StackMap && openId) {
        console.log("calling StackMap.destroy", openId);
        StackMap.destroy(openId);
      }
    }
  );

  function getOrCreateShadow() {
    const shadowHostId = "my-shadow-host";
    let shadowHost = document.getElementById(shadowHostId);

    // If it doesn't exist, create it
    if (!shadowHost) {
      shadowHost = document.createElement("div");
      shadowHost.id = shadowHostId;
      document.body.appendChild(shadowHost);
    }

    // Attach or retrieve the Shadow DOM root
    const existingShadowRoot = shadowHost.shadowRoot;
    const newShadowRoot =
      existingShadowRoot || shadowHost.attachShadow({ mode: "open" });
    stackMapDialogContainer = newShadowRoot.getElementById(
      "stackMapDialogContainer"
    );
    const handleDestroy = (event: Event) => {
      console.log("SM_destroy event triggered", event);
      openId = null;
      // TODO Somehow communicate to the component containing the map it button to refocus
    };

    if (!stackMapDialogContainer) {
      stackMapDialogContainer = document.createElement("div");
      stackMapDialogContainer.id = "stackMapDialogContainer";
      stackMapDialogContainer.addEventListener("SM_destroy", handleDestroy);
      newShadowRoot.appendChild(stackMapDialogContainer);
    }

    return {
      shadowRoot: newShadowRoot,
      dialogContainer: stackMapDialogContainer,
    };
  }

  return {
    getScript: async (url: string) => {
      const { shadowRoot, dialogContainer } = getOrCreateShadow();

      if (StackMap) {
        return { StackMap, shadowRoot, dialogContainer };
      }
      if (pendingScriptRequest) {
        return pendingScriptRequest;
      }

      pendingScriptRequest = new Promise((resolve, reject) => {
        var script = document.createElement("script");
        script.src = url;
        script.async = true;
        script.onload = async () => {
          // console.log('window.StackMap: ', window.StackMap)
          StackMap = window.StackMap;
          try {
            console.log("Calling StackMap.init", dialogContainer);
            const response = await StackMap.init(dialogContainer);
            console.log("Response", response);
            resolve({ StackMap, shadowRoot, dialogContainer });
          } catch (e) {
            console.error(e);
            reject(new Error("Error calling StackMap.init"));
          }
        };

        script.onerror = () => {
          reject(new Error("Error loading StackMap script"));
        };

        shadowRoot.appendChild(script); // Or document.body.appendChild(script);
      });

      return pendingScriptRequest;
    },
    openMapItDialog: (uuid: SMResult["uuid"]) => {
      console.log("openMapItDialog");
      if (!StackMap) {
        throw "Please wait for StackMap to load";
      }
      console.log("Setting StackMap uuid", uuid);
      openId = uuid;
      const result = SMResultMap.get(uuid);
      try {
        console.log(
          "Calling StackMap.render",
          { results: [result] },
          stackMapDialogContainer
        );
        StackMap.render({ results: [result] }, stackMapDialogContainer);
      } catch (e) {
        console.log(e);
      }
    },
    // API method to send holdings and availability and return whether to display a Map It button
    getDisplayMapItButtons: async (request: StackMapShadowData[]) => {
      if (!StackMap) {
        throw "Please wait for StackMap to load";
      }
      console.log("Calling StackMap.getMapItData", request);
      try {
        const { results } = await StackMap.getMapItData(request);
        const displayButton = new Map<SMResult["uuid"], boolean>();
        results.forEach((result) => {
          console.log("result: ", result);
          if (result.valid) {
            displayButton.set(result.uuid, result.maps?.length > 0);
            SMResultMap.set(result.uuid, result);
          } else {
            displayButton.set(result.uuid, false);
          }
        });
        return displayButton;
      } catch (e) {
        console.error(e);
      }
    },
  };
})();

export const StackMapProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  console.log("Render StackMap provider");
  const [StackMap, setStackMap] = useState<Stackmap | null>(null);
  const { locationConfiguration } = useLocationConfiguration();
  const { stackMapEnabled, stackMapUrl } =
    locationConfiguration.pacSettings.stackMapSettings;
  // I don't know that we truly need a state object for this container...
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  React.useEffect(() => {
    console.log("StackMapSettings", stackMapEnabled, stackMapUrl);
    // Load the StackMap script using the StackMapScriptHolder

    const loadScript = async () => {
      try {
        const { StackMap } = await StackMapScriptHolder.getScript(stackMapUrl); // const url = "https://www.stackmapintegration.com/demo-tlc/StackMap.js";
        setStackMap(StackMap);
      } catch (err: unknown) {
        console.error(err);
        if (err instanceof Error) {
          setError(err);
        } else if (typeof err === "string") {
          setError(new Error(err));
        } else {
          setError(new Error("An unknown error occurred."));
        }
      } finally {
        setLoading(false);
      }
    };

    if (stackMapEnabled) {
      loadScript();
    }

    return () => {};
  }, []);

  return (
    <StackMapContext.Provider
      value={{
        StackMap,
        getDisplayMapItButtons: StackMapScriptHolder.getDisplayMapItButtons,
        openMapItDialog: StackMapScriptHolder.openMapItDialog,
        loading,
        error,
        stackMapEnabled,
      }}
    >
      {children}
    </StackMapContext.Provider>
  );
};

// Custom hook to use the StackMap context
export const useStackMap = () => {
  const context = useContext(StackMapContext);
  if (!context) {
    throw new Error("useStackMap must be used within a StackMapProvider");
  }
  return context;
};

export const useDisplayMapItButton = (
  holdings: HoldingsInformation[],
  itemAvailability: Map<string, ItemAvailability>,
  itemAvailabilityReady: boolean,
  resource: ResourceDWR
) => {
  const { getDisplayMapItButtons, loading, error } = useStackMap();
  const [displayMapItButton, setDisplayMapItButton] = useState<
    Map<SMResult["uuid"], boolean>
  >(new Map());

  React.useEffect(() => {
    async function fetchDisplayMapItButtons() {
      try {
        const stackMapShadowData: StackMapShadowData[] = holdings.map(
          (holding) => {
            const availability = itemAvailability.get(holding.barcode);
            return {
              ...holding,
              ...availability,
              statusDescription: availability.status,
              uuid: holding.id,
              title: resource.shortTitle,
              author: resource.shortAuthor,
            };
          }
        );
        const mapItButtons = await getDisplayMapItButtons(stackMapShadowData);
        setDisplayMapItButton(mapItButtons);
      } catch (e) {
        console.error("Error fetching displayMapItButtons:", e);
      }
    }

    if (
      !loading &&
      error === null &&
      holdings.length > 0 &&
      itemAvailabilityReady
    ) {
      fetchDisplayMapItButtons();
    }
  }, [
    holdings,
    itemAvailability,
    itemAvailabilityReady,
    resource,
    getDisplayMapItButtons,
    loading,
    error,
  ]);

  return displayMapItButton;
};
